import { faChevronDown, faLayerGroup } from "@fortawesome/pro-solid-svg-icons";
import { getMoneyValue } from "@tosize/shapes/src/lib/utils";
import {
	Anchor,
	Button,
	DialogButton,
	ProductGroup,
	ProductionUnit,
	Typography,
	formatDimensions,
	formatNumber,
	formatUnit,
	getLocaleAsset,
} from "@tosize/ui";
import { Tabs } from "radix-ui";
import { useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useCardContext } from "../../../context/CardContext";
import { useConfig } from "../../../context/ConfigContext";
import { usePostDataContext } from "../../../context/PostDataContext";
import useProducts from "../../../hooks/useProducts";
import useShapes from "../../../hooks/useShapes";
import { Step } from "../../Steps/Step";
import { TextDivider } from "../../ui/TextDivider";
import { USP } from "../Fields/USP";
import { TabsTrigger } from "../TabsTrigger";
import { SHAPE_DIMENSIONS_STEP_IDENTIFIER } from "./ShapeDimensions";

export const TYPE_STEP_IDENTIFIER = "production_type";

export function ProductionType() {
	const intl = useIntl();
	const { product, productData, defaultPanel } = useProducts();
	const { setCurrentShape } = useShapes();
	const { navigateToCard } = useCardContext();
	const { productGroup, routes } = useConfig();
	const { data, setData } = usePostDataContext();

	const units = productData?.available_production_units;

	useEffect(() => {
		if (
			units !== undefined &&
			data.unit === undefined &&
			productGroup !== ProductGroup.Plastic_Sheet
		) {
			setData((prev) => ({
				...prev,
				unit: units.find((unit) => unit === ProductionUnit.CompletePanel) ?? ProductionUnit.M2,
			}));

			setCurrentShape({ shape: "plate_rectangle_custom" });
		}
	}, [units, data.unit, productGroup, setData, setCurrentShape]);

	const onValueChange = (value: string) => {
		setData((prev) => ({ ...prev, unit: value as ProductionUnit }));

		setCurrentShape({ shape: "plate_rectangle_custom", unit: value, scroll: true });

		navigateToCard(TYPE_STEP_IDENTIFIER, SHAPE_DIMENSIONS_STEP_IDENTIFIER, true);
	};

	const price = productData?.panelSizes?.find((panelSize) => panelSize.isDefault)?.priceM2;

	const completePrice = productData?.panelSizes?.length
		? productData.panelSizes.reduce((prev, current) =>
				current.price.includingTax < prev.price.includingTax ? current : prev,
			).price
		: undefined;

	const completePanelPrice = useMemo(() => {
		if (defaultPanel && productData?.checkModeEnabled) {
			const panelSizes = productData.panelSizes?.filter(
				(panel) => panel.length >= defaultPanel.length && panel.width >= defaultPanel.width,
			);

			const moneyComplete = panelSizes?.reduce((prev, current) => {
				const prevPrice = getMoneyValue(prev.price);
				const currentPrice = getMoneyValue(current.price);

				return currentPrice < prevPrice ? current : prev;
			});

			if (moneyComplete?.price) {
				return {
					...moneyComplete.price,
					excludingTax:
						(defaultPanel.length / 1000) *
						(defaultPanel.width / 1000) *
						moneyComplete.price.excludingTax,
					includingTax:
						(defaultPanel.length / 1000) *
						(defaultPanel.width / 1000) *
						moneyComplete.price.includingTax,
				};
			}

			return undefined;
		}

		return undefined;
	}, [productData, defaultPanel]);

	if (productGroup === ProductGroup.Plastic_Sheet) {
		return null;
	}

	return (
		<Step
			title={intl.formatMessage({ defaultMessage: "Afnametype", id: "FqM7K8" })}
			value={TYPE_STEP_IDENTIFIER}
			disabled={!product}
			selected={!!data.unit}
			selectedValue={
				data.unit
					? intl.formatMessage(
							{ defaultMessage: "Koop per {unit}", id: "ylvQW1" },
							{ unit: formatUnit(intl, data.unit) },
						)
					: undefined
			}
			className="space-y-4"
		>
			<div className="leading-tight">
				<Typography className="inline">
					<FormattedMessage
						defaultMessage="Kies de afname die het beste bij jouw project past."
						id="EbRbxv"
					/>
				</Typography>
				&nbsp;
				{routes.get("customer_service/order/place_order") && (
					<Anchor
						href={routes.get("customer_service/order/place_order")}
						target="_blank"
						className="inline"
					>
						<FormattedMessage defaultMessage="Lees hier hoe het werkt." id="m/TbNd" />
					</Anchor>
				)}
			</div>

			<Tabs.Root
				value={data.unit}
				onValueChange={(value) => setData((prev) => ({ ...prev, unit: value as ProductionUnit }))}
				className="space-y-4"
			>
				<Tabs.List className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:grid-cols-[1fr,4rem,1fr] sm:gap-0 lg:grid-cols-2 lg:gap-2 xl:grid-cols-[1fr,4rem,1fr] xl:gap-0">
					<TabsTrigger
						variant="type"
						onValueChange={onValueChange}
						value={ProductionUnit.CompletePanel}
						title={intl.formatMessage(
							{ defaultMessage: "Koop per {unit}", id: "ylvQW1" },
							{
								unit: formatUnit(intl, ProductionUnit.CompletePanel),
							},
						)}
						image={getLocaleAsset(
							`panel-configurator/production_type_${ProductionUnit.CompletePanel}.png`,
						)}
						description={
							<>
								{intl.formatMessage({
									defaultMessage:
										"Alle items worden efficiënt uit een plaat gezaagd. Restmateriaal wordt optioneel meegeleverd.",
									id: "83iuU3",
								})}
								<br />
								{intl.formatMessage({ defaultMessage: "Minimale afname", id: "9ggn+B" })}:{" "}
								{intl.formatMessage({ defaultMessage: "een hele plaat", id: "gXfons" })}
							</>
						}
						price={completePanelPrice ?? completePrice}
						_priceFrom={productData?.panelSizes && productData.panelSizes.length > 1}
						disabled={!units?.find((unit) => unit === ProductionUnit.CompletePanel)}
					/>

					<TextDivider variant="vertical" className="hidden sm:flex lg:hidden xl:flex">
						<FormattedMessage defaultMessage="Of" id="zg3046" />
					</TextDivider>

					<TabsTrigger
						variant="type"
						onValueChange={onValueChange}
						value={ProductionUnit.M2}
						title={intl.formatMessage(
							{ defaultMessage: "Koop per {unit}", id: "ylvQW1" },
							{
								unit: formatUnit(intl, ProductionUnit.M2),
							},
						)}
						image={getLocaleAsset(`panel-configurator/production_type_${ProductionUnit.M2}.png`)}
						description={
							<>
								{intl.formatMessage({
									defaultMessage: "Neem enkel af wat je nodig hebt, geen restmateriaal.",
									id: "+s2FrC",
								})}
								<br />
								{intl.formatMessage({ defaultMessage: "Minimale afname", id: "9ggn+B" })}:{" "}
								{intl.formatMessage({ defaultMessage: "geen", id: "V0kWzE" })}
							</>
						}
						price={price}
						disabled={
							!units?.find((unit) => unit === ProductionUnit.M2) && Number(units?.length) >= 1
						}
					/>
				</Tabs.List>
			</Tabs.Root>

			<div className="mt-4 flex xs:flex-row flex-col xs:items-start items-end justify-end xs:justify-between gap-4">
				<div className="flex flex-col gap-x-4 gap-y-1 leading-tight">
					{productData?.panelSizes && productData.panelSizes.length > 1 && (
						<DialogButton
							width="2xl"
							title={intl.formatMessage({
								defaultMessage: "Beschikbare plaatgroottes voor deze dikte",
								id: "M/bzXj",
							})}
							content={
								<div className="flex flex-col items-center gap-4">
									<Typography>
										{intl.formatMessage({
											defaultMessage:
												"Met deze optie neem je een hele plaat af. De plaatgrootte wordt automatisch geselecteerd in de winkelwagen op basis van de ingevoerde items in de volgende stap. In de winkelwagen wordt de kleinste plaat geselecteerd waar alle items uit jouw zaaglijst in passen. Je kan zelf geen invloed uitoefenen op de selectie van een plaat.",
											id: "Q0dbgv",
										})}
									</Typography>

									<div className="scrollbar-none w-full overflow-x-auto">
										<table className="w-full whitespace-nowrap">
											<thead>
												<tr className="bg-primary-light *:px-4 *:py-2 *:text-left *:leading-tight">
													<th>
														{intl.formatMessage({
															defaultMessage: "Afmetingen (cm)",
															id: "oGXugP",
														})}
													</th>
													<th>
														{intl.formatMessage(
															{ defaultMessage: "Prijs per {unit}", id: "ck173K" },
															{ unit: ProductionUnit.M2 },
														)}
													</th>
												</tr>
											</thead>

											<tbody className="even:*:bg-shades-light">
												{productData.panelSizes.map((panelSize) => (
													<tr key={panelSize.id} className="*:px-4 *:py-2 *:leading-tight">
														<td>
															{formatDimensions({
																lang: intl.locale,
																unit: "centimeter",
																values: [panelSize.length / 10, panelSize.width / 10],
															})}
														</td>
														<td>
															{formatNumber({
																lang: intl.locale,
																value: getMoneyValue(panelSize.price),
																digits: { min: 2 },
															})}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>

									<Typography>
										{intl.formatMessage({
											defaultMessage:
												"In sommige gevallen zijn andere maten beschikbaar. Deze worden automatisch gekozen als ze efficiënter plaatgebruik mogelijk maken.",
											id: "pv3158",
										})}
									</Typography>

									<Button
										color="success"
										onClick={() => {
											const unit =
												units?.find((unit) => unit === ProductionUnit.CompletePanel) ??
												ProductionUnit.M2;

											onValueChange(unit);
										}}
										disabled={!product}
									>
										<FormattedMessage defaultMessage="Afmetingen opgeven" id="RWgY1j" />
									</Button>
								</div>
							}
						>
							<Button variant="link" className="leading-tight" starticon={faLayerGroup}>
								<FormattedMessage defaultMessage="Bekijk beschikbare plaatgroottes" id="URY0GA" />
							</Button>
						</DialogButton>
					)}

					<USP />
				</div>

				<Button
					color="success"
					endicon={faChevronDown}
					onClick={() => {
						const unit =
							units?.find((unit) => unit === ProductionUnit.CompletePanel) ?? ProductionUnit.M2;

						onValueChange(unit);
					}}
					disabled={!product}
					className="ml-auto shrink-0"
				>
					<FormattedMessage defaultMessage="Afmetingen opgeven" id="RWgY1j" />
				</Button>
			</div>
		</Step>
	);
}
